import { Component, Input, OnChanges } from "@angular/core";

export interface TreemapNode {
  label: string;
  value: number;
  meta?: string;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
  node: TreemapNode;
}

@Component({
  selector: "app-exp-treemap",
  standalone: true,
  imports: [],
  template: `
    <div class="treemap" #wrap>
      @for (r of rects; track r) {
        <div
          class="tm-cell"
          [class.small]="r.w * r.h < 6000"
          [style.left.%]="r.x"
          [style.top.%]="r.y"
          [style.width.%]="r.w"
          [style.height.%]="r.h"
          [style.background]="bg(r.node.value)"
          [title]="r.node.label + ': ' + format(r.node.value)"
        >
          <div class="tm-ticker">{{ r.node.label }}</div>
          <div class="tm-val">{{ format(r.node.value) }}</div>
        </div>
      }
    </div>
  `,
})
export class TreemapComponent implements OnChanges {
  @Input() nodes: TreemapNode[] = [];
  @Input() format: (v: number) => string = (v) => (v * 100).toFixed(1) + "%";
  @Input() colorScale: string[] = [
    "#4A2FB5",
    "#6A49E5",
    "#7C5CFF",
    "#9B7FFF",
    "#C2B0FF",
  ];

  rects: Rect[] = [];
  private max = 1;

  ngOnChanges(): void {
    const items = [...this.nodes]
      .filter((n) => n.value > 0)
      .sort((a, b) => b.value - a.value);
    if (!items.length) {
      this.rects = [];
      return;
    }
    this.max = items[0].value;
    const total = items.reduce((s, n) => s + n.value, 0);
    this.rects = this.squarify(items, { x: 0, y: 0, w: 100, h: 100 }, total);
  }

  bg(v: number): string {
    const t = Math.min(1, v / this.max);
    const idx = Math.floor(t * (this.colorScale.length - 1));
    return this.colorScale[
      Math.max(0, Math.min(this.colorScale.length - 1, idx))
    ];
  }

  private squarify(
    items: TreemapNode[],
    bounds: { x: number; y: number; w: number; h: number },
    totalValue: number,
  ): Rect[] {
    const out: Rect[] = [];
    let remaining = [...items];
    let area = bounds.w * bounds.h;
    let value = totalValue;
    let cursor = { ...bounds };

    while (remaining.length) {
      const row: TreemapNode[] = [];
      const side = Math.min(cursor.w, cursor.h);
      const horizontal = cursor.w >= cursor.h;

      const scaleToArea = () =>
        row.reduce((s, n) => s + (n.value / value) * area, 0);

      let best = Infinity;
      while (remaining.length) {
        const next = remaining[0];
        const candidate = [...row, next];
        const rowArea = candidate.reduce(
          (s, n) => s + (n.value / value) * area,
          0,
        );
        const worst = Math.max(
          ...candidate.map((n) => {
            const a = (n.value / value) * area;
            const w = rowArea / side;
            return Math.max(
              (side * side * a) / (rowArea * rowArea),
              (rowArea * rowArea) / (side * side * a),
            );
          }),
        );
        if (worst > best && row.length) break;
        best = worst;
        row.push(next);
        remaining.shift();
        if (!remaining.length) break;
      }

      const rowArea = scaleToArea();
      if (horizontal) {
        const rowH = rowArea / cursor.w;
        let x = cursor.x;
        for (const n of row) {
          const w = ((n.value / value) * area) / rowH;
          out.push({ x, y: cursor.y, w, h: rowH, node: n });
          x += w;
        }
        cursor = {
          x: cursor.x,
          y: cursor.y + rowH,
          w: cursor.w,
          h: cursor.h - rowH,
        };
      } else {
        const rowW = rowArea / cursor.h;
        let y = cursor.y;
        for (const n of row) {
          const h = ((n.value / value) * area) / rowW;
          out.push({ x: cursor.x, y, w: rowW, h, node: n });
          y += h;
        }
        cursor = {
          x: cursor.x + rowW,
          y: cursor.y,
          w: cursor.w - rowW,
          h: cursor.h,
        };
      }
      area = cursor.w * cursor.h;
      value = remaining.reduce((s, n) => s + n.value, 0) || 1;
      if (cursor.w < 0.1 || cursor.h < 0.1) break;
    }

    return out;
  }
}
